import { useState, useCallback } from 'react';
import { useClipboard } from '../contexts/ClipboardContext';
import { renameFolderApi, createFolderApi, deleteFolderApi, moveProjectApi, copyProjectApi, getFolderBreadcrumb, setFolderDefaultClid, removeFolderDefaultClid } from '../services/folder.service';
import type { Folder } from '../services/folder.service';
import { findFolder, findProjectParent } from '../helpers/folder.utils';
import { parseNodeKey } from '../utils/nodeId';
import { deleteProjects, exportProjects, importProjectData, getAllProjectList } from '../services/project.service';
import { copyTestCaseApi } from '../services/testcase.service';
import { useDispatch } from 'react-redux';
import { toggleAlert, toggleSpinner, triggerProjectTestCasesRefresh } from '../store/resuders/app.reducer';
import { useTranslation } from 'react-i18next';

// Define props the hook needs
interface FolderOperationsProps {
  folders: Folder[]; // Needed for finding parent names/IDs
  refetchFolders: (showLoading?: boolean) => Promise<void>;
  renamingNodeId: string; // Needed for rename submit
  renameValue: string; // Needed for rename submit
  onRenameCancel: () => void; // To cancel rename UI after submit
  setSelectedNodeIds: (ids: string[]) => void; // To clear selection after delete
  selectedNodeIds: string[]; // To check if deleted item was selected
  // Add setters for loaded test cases and clipboard if needed after delete
  clearLoadedTestCasesForProject: (projectIds: number[]) => void;
  refetchAllProjects: (setLoading?: boolean) => Promise<void>; // Add the new prop type
  setExpandedNodeIds: (ids: string[]) => void;
  loadedTestCases: Record<number, any>;
  // Edit Default CLID modal state setter
  setEditClidModal: React.Dispatch<React.SetStateAction<{
    isOpen: boolean;
    folderId?: number;
    folderName?: string;
    initialClid?: string | null;
    loading?: boolean;
  }>>;
}

export function useFolderOperations({
  folders,
  refetchFolders,
  renamingNodeId,
  renameValue,
  onRenameCancel,
  setSelectedNodeIds,
  selectedNodeIds,
  clearLoadedTestCasesForProject,
  refetchAllProjects, // Add the new prop
  setExpandedNodeIds,
  loadedTestCases,
  setEditClidModal,
}: FolderOperationsProps) {
  const { mode, items, copy, cut, clear: clearClipboard } = useClipboard();
  const [isCreatingFolder, setIsCreatingFolder] = useState<boolean>(false);
  const [newFolderParentId, setNewFolderParentId] = useState<number | null | undefined>(undefined);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // --- Create Folder Logic ---
  const openCreateFolderModal = useCallback((nodeId: string) => {
    let parentId: number | null = null;
    if (nodeId !== 'folder-root') {
      try {
          parentId = parseNodeKey(nodeId).id;
      } catch (e) {
          console.error(`Error parsing parent node ID for new folder: ${nodeId}`, e);
          // Decide how to handle - maybe default to root or show error?
          parentId = 0; // Defaulting to root for now
      }
    }else{
      parentId = 0; // Defaulting to root for now
    }
    setNewFolderParentId(parentId);
    setIsCreatingFolder(true);
  }, []); // No dependencies needed

  const handleCreateCancel = useCallback(() => {
    setIsCreatingFolder(false);
    setNewFolderParentId(undefined);
  }, []);

  const handleCreateSubmit = useCallback(async (name: string) => {
    if (newFolderParentId === undefined) {
      console.error("Cannot create folder without a parent ID context.");
      return;
    }
    setIsCreatingFolder(false);
    const parentId = newFolderParentId;
    setNewFolderParentId(undefined);

    try {
      await createFolderApi(name, parentId);
      await refetchFolders(false);
      // TODO: Optionally find the new folder and select it or expand the parent
    } catch (error) {
      console.error('Failed to create folder:', error);
      // TODO: Show error to user (e.g., Snackbar)
      // Safely access error message
      const message = error instanceof Error ? error.message : 'Please try again.';
      alert(`Failed to create folder: ${message}`);
    }
  }, [newFolderParentId, refetchFolders]);

  const getParentFolderName = useCallback((parentId: number | null): string | undefined => {
    if (parentId === null || parentId === 0) return undefined; // Root
    const parentFolder = findFolder(folders, parentId);
    return parentFolder?.name;
  }, [folders]); // Depends on current folder structure

  // --- Rename Folder Logic ---
  const handleRenameSubmit = useCallback(async () => {
    if (!renamingNodeId) return;
    let id: number;
    try {
        id = parseNodeKey(renamingNodeId).id;
    } catch(e) {
        console.error(`Error parsing node ID for rename submit: ${renamingNodeId}`, e);
        onRenameCancel(); // Cancel the rename UI
        return;
    }
    const submittedName = renameValue.trim(); // Trim whitespace
    if (!submittedName) {
        alert("Folder name cannot be empty."); // Basic validation
        return; // Don't submit empty names
    }
    onRenameCancel(); // Optimistically close rename UI

    try {
      await renameFolderApi(id, submittedName);
      await refetchFolders(false);
    } catch (error) {
      console.error('Rename failed', error);
      // Safely access error message
      const message = error instanceof Error ? error.message : 'Please try again.';
      alert(`Failed to rename folder: ${message}`);
      // Maybe re-open rename UI or show persistent error?
    }
  }, [renamingNodeId, renameValue, onRenameCancel, refetchFolders]);


  // --- Delete Folder Logic ---
  const handleDeleteFolder = useCallback(async (nodeId: string) => {
    let folderId: number;
     try {
        folderId = parseNodeKey(nodeId).id;
    } catch(e) {
        console.error(`Error parsing node ID for delete folder: ${nodeId}`, e);
        return;
    }
    const folderToDelete = findFolder(folders, folderId);
    const folderName = folderToDelete ? folderToDelete.name : 'this folder';

    if (window.confirm(`Are you sure you want to delete '${folderName}'? This action cannot be undone.`)) {
      try {
        await deleteFolderApi(folderId);
        await refetchFolders(false);
        // If the deleted folder was selected, clear the selection
        if (selectedNodeIds.includes(nodeId)) {
          setSelectedNodeIds([]); // Clear selection entirely or filter out the specific ID
          // Also clear details panel if needed (handled in Folders.tsx or useFolderSelectionDetails)
        }
      } catch (error: any) {
        console.error('Failed to delete folder:', error);
        let message = 'Could not delete folder. An unexpected error occurred.';
        // Check specific properties common in API errors
        if (error?.response?.status === 400) {
            const apiMessage = error.response?.data?.message || error.response?.data?.detail;
            message = `Could not delete folder: ${apiMessage || 'Folder might not be empty or is protected.'}`;
        } else if (error?.response?.status === 404) {
            message = 'Could not delete folder: Folder not found.';
        } else if (error instanceof Error) {
            // Fallback to standard Error message if specific checks fail
            message = `Could not delete folder: ${error.message}`;
        }
        alert(message); // Replace with a proper UI notification
      }
    }
  }, [folders, refetchFolders, selectedNodeIds, setSelectedNodeIds]); // Depends on folder data and selection state

  // --- Clipboard Actions ---
  const handleCopyProjects = useCallback((projectIds: number[]) => {
    copy(projectIds.map((id) => ({ kind: 'project', id })));
  }, [copy]);

  const handleCutProjects = useCallback((projectIds: number[], _srcFolderId?: number) => {
    // srcFolderId might be useful if API requires it for cutting, but clipboard only stores item kind/id
    cut(projectIds.map((id) => ({ kind: 'project', id })));
  }, [cut]);

  const handleCopyTestCases = useCallback((testCaseIds: number[], sourceProjectId: number) => {
    copy(testCaseIds.map((id) => ({ kind: 'testcase', id, sourceProjectId })));
  }, [copy]);

  const handlePasteItems = useCallback(async (destNodeId: string) => {
    if (!mode || items.length === 0) return;
    
    const { type: destType, id: destId } = parseNodeKey(destNodeId);
    
    // Determine target project ID based on destination type
    let targetProjectId: number;
    if (destType === 'project') {
      targetProjectId = destId;
    } else if (destType === 'folder') {
      // For folder destinations, only handle project pasting
      const destFolderId = destNodeId === 'folder-root' ? 0 : destId;
      
      try {
        if (mode === 'copy') {
          await Promise.all(items.filter(i => i.kind === 'project').map(item =>
            copyProjectApi(item.id, destFolderId)
          ));
        } else if (mode === 'cut') {
          await Promise.all(items.filter(i => i.kind === 'project').map(item => {
            const srcFolderId = findProjectParent(folders, item.id) ?? 0;
            return moveProjectApi(item.id, srcFolderId, destFolderId);
          }));
        }
        clearClipboard();
        await refetchFolders(false);
      } catch (error) {
        console.error('Failed to paste projects:', error);
        const message = error instanceof Error ? error.message : 'An error occurred.';
        alert(`Failed to paste: ${message}`);
      }
      return;
    } else {
      console.error(`Invalid destination type for paste: ${destType}`);
      alert(`Error: Invalid destination selected for paste.`);
      return;
    }

    // Handle testcase pasting to project
    const testCaseItems = items.filter(i => i.kind === 'testcase');
    if (testCaseItems.length === 0) return;

    try {
      if (mode === 'copy') {
        await Promise.all(testCaseItems.map(item => {
          if (!item.sourceProjectId) {
            throw new Error(`Missing source project ID for testcase ${item.id}`);
          }
          return copyTestCaseApi(item.sourceProjectId, item.id, targetProjectId);
        }));
        
        // Clear clipboard and refresh target project's testcase list
        clearClipboard();
        
        // Use Redux action to trigger selective refresh (same pattern as create/delete/update)
        dispatch(triggerProjectTestCasesRefresh({ projectId: targetProjectId }));
        
        // Show success message
        const count = testCaseItems.length;
        const message = count === 1 ? 'Test case copied successfully' : `${count} test cases copied successfully`;
        dispatch(toggleAlert({ message, severity: 'success' }));
        
      } else if (mode === 'cut') {
        // Cut operation for testcases would require a move API endpoint
        // For now, just clear the clipboard to prevent confusion
        clearClipboard();
        alert('Cut operation is not supported for test cases. Please use copy instead.');
      }
    } catch (error) {
      console.error('Failed to paste test cases:', error);
      const message = error instanceof Error ? error.message : 'An error occurred.';
      alert(`Failed to paste test cases: ${message}`);
    }
  }, [mode, items, folders, clearClipboard, refetchFolders, clearLoadedTestCasesForProject, dispatch]);


  // --- Delete Projects ---
  const handleDeleteProjects = useCallback(async (projectIds: number[]) => {
    if (projectIds.length === 0) return;
    const message = projectIds.length > 1
      ? `Are you sure you want to delete these ${projectIds.length} projects? This action cannot be undone.`
      : 'Are you sure you want to delete this project? This action cannot be undone.';

    if (!window.confirm(message)) return;

    try {
      await deleteProjects(projectIds);
      await refetchFolders(false);
      await refetchAllProjects(false); // Call the refetch function here

      // Clear selection if any deleted projects (or their test cases) were selected
      const remainingSelected = selectedNodeIds.filter(id => {
          try {
              const { type, id: selectedId } = parseNodeKey(id);
              if (type === 'project') {
                  return !projectIds.includes(selectedId);
              }
              // If we could determine the parent project ID for a test case here,
              // we could also filter out selected test cases whose parent project was deleted.
              // This requires enhancing how selection state is managed or passing parent info.
              // For now, we only clear selected projects.
              return true; // Keep non-project selections
          } catch {
              return true; // Keep invalid/unparseable IDs? Or filter them?
          }
      });
      setSelectedNodeIds(remainingSelected);

      // Clear clipboard state if deleted items were cut/copied
      if (mode === 'copy' || mode === 'cut') {
          const remainingItems = items.filter(item =>
              item.kind !== 'project' || !projectIds.includes(item.id)
          );
          if (remainingItems.length < items.length) {
              if (remainingItems.length === 0) {
                  clearClipboard();
              } else if (mode === 'copy') {
                  copy(remainingItems);
              } else {
                  cut(remainingItems);
              }
          }
      }

      // Clear any loaded test cases for the deleted projects
      clearLoadedTestCasesForProject(projectIds);

    } catch (error) {
      console.error('Failed to delete project(s):', error);
      // Safely access error message
      const message = error instanceof Error ? error.message : 'Please try again.';
      alert(`Failed to delete project(s): ${message}`);
    }
  }, [selectedNodeIds, refetchFolders, refetchAllProjects, setSelectedNodeIds, mode, items, copy, cut, clearClipboard, clearLoadedTestCasesForProject]); // Add refetchAllProjects to dependencies

  // --- Export Projects ---
  const handleExportProjects = useCallback(async (projectIds: number[]) => {
    if (projectIds.length === 0) return;
    await exportProjects(projectIds);
  }, []);


  // --- Refresh All ---
  const handleRefreshAll = useCallback(async () => {
    // Preserve current selection to restore after reload
    const prevSelection = [...selectedNodeIds];

    // Clear any stored state so the tree collapses like a fresh load
    sessionStorage.removeItem('folderViewSessionState');
    sessionStorage.removeItem('folderViewTestCaseState');

    // Reset local state
    setSelectedNodeIds([]);
    setExpandedNodeIds(['folder-root']);
    clearClipboard();
    clearLoadedTestCasesForProject(Object.keys(loadedTestCases).map(Number));

    // Refetch folders and projects
    await refetchFolders(true);
    await refetchAllProjects(true);

    // Restore selection so active item stays highlighted
    if (prevSelection.length > 0) {
      setSelectedNodeIds(prevSelection);
    }
  }, [
    selectedNodeIds,
    refetchFolders,
    refetchAllProjects,
    setSelectedNodeIds,
    setExpandedNodeIds,
    clearClipboard,
    clearLoadedTestCasesForProject,
    loadedTestCases,
  ]);

  // --- Import Project Logic ---
  const handleImportProject = useCallback((nodeId: string) => {
    let folderId: number;
    try {
      folderId = nodeId === 'folder-root' ? 0 : parseNodeKey(nodeId).id;
    } catch(e) {
      console.error(`Error parsing node ID for import: ${nodeId}`, e);
      return;
    }
    
    // Create file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      
      // Show loading spinner
      dispatch(toggleSpinner(true));
      
      try {
        // Get list of existing project IDs before import
        const existingProjectsResponse = await getAllProjectList();
        const existingProjectIds = existingProjectsResponse?.data?.map((p: any) => p.testProjectID) || [];

        // Import the project
        const result: any = await importProjectData(file);

        if (result?.code === "success") {
          // Get updated list of projects to find newly imported ones
          const updatedProjectsResponse = await getAllProjectList();
          const updatedProjects = updatedProjectsResponse?.data || [];
          
          // Find newly imported projects
          const newProjects = updatedProjects.filter((p: any) => 
            !existingProjectIds.includes(p.testProjectID)
          );

          // Move each newly imported project to the target folder
          if (newProjects.length > 0 && folderId !== 0) {
            await Promise.all(
              newProjects.map((project: any) => 
                moveProjectApi(project.testProjectID, 0, folderId)
              )
            );
          }

          dispatch(toggleAlert({ message: t("Project imported successfully"), severity: 'success' }));
          await refetchFolders(false);
          await refetchAllProjects(false);
        } else {
          const errorMessage = result?.data?.messages?.join(',') || t("An error occurred during import.");
          dispatch(toggleAlert(errorMessage));
        }
      } catch (error) {
        dispatch(toggleAlert(t("An unexpected error occurred. Please try again.")));
      } finally {
        // Always hide spinner when done
        dispatch(toggleSpinner(false));
      }
    };
    
    input.click();
  }, [folders, dispatch, t, refetchFolders, refetchAllProjects]);

  // --- Edit Default CLID Logic ---
  const handleEditDefaultClid = useCallback(async (nodeId: string) => {
    let folderId: number;
    try {
      folderId = parseNodeKey(nodeId).id;
    } catch(e) {
      console.error(`Error parsing node ID for edit default CLID: ${nodeId}`, e);
      return;
    }

    try {
      // Get current CLID value from breadcrumb API
      const breadcrumb = await getFolderBreadcrumb(folderId);
      const folderData = breadcrumb[breadcrumb.length - 1]; // Last item contains target folder info
      const currentClid = folderData?.clid || null;
      const folderName = folderData?.name || '';

      // Open modal with current values
      setEditClidModal({
        isOpen: true,
        folderId,
        folderName,
        initialClid: currentClid,
      });

    } catch (error) {
      console.error('Failed to load folder CLID:', error);
      const message = error instanceof Error ? error.message : 'Please try again.';
      alert(`Failed to load folder CLID: ${message}`);
    }
  }, [setEditClidModal]);

  // --- Submit CLID Changes ---
  const handleEditClidSubmit = useCallback(async (folderId: number, newClid: string, initialClid: string | null) => {
    const trimmedClid = newClid.trim();
    
    // Set loading state
    setEditClidModal(prev => ({ ...prev, loading: true }));
    
    try {
      if (trimmedClid === '' && initialClid !== null && initialClid !== '') {
        // User cleared the field and there was a previous value - DELETE
        await removeFolderDefaultClid(folderId);
      } else if (trimmedClid !== '') {
        // User entered a value - POST
        await setFolderDefaultClid(folderId, trimmedClid);
      }
      // If trimmedClid is empty and initialClid was already empty, do nothing
      
      await refetchFolders(false); // Refresh folder data
      
      // Close modal
      setEditClidModal({ isOpen: false });
      
      // Show success message
      const action = trimmedClid === '' ? 'removed' : 'updated';
      dispatch(toggleAlert({ message: `Default CLID ${action} successfully`, severity: 'success' }));
      
    } catch (error) {
      console.error('Failed to update folder CLID:', error);
      const message = error instanceof Error ? error.message : 'Please try again.';
      dispatch(toggleAlert(`Failed to update folder CLID: ${message}`));
      
      // Clear loading state on error but keep modal open
      setEditClidModal(prev => ({ ...prev, loading: false }));
    }
  }, [refetchFolders, setEditClidModal, dispatch]);

  return {
    // Create Folder State & Handlers
    isCreatingFolder,
    openCreateFolderModal, // Renamed from handleNewFolder
    handleCreateCancel,
    handleCreateSubmit,
    getParentFolderName,
    // Rename Folder Submit Handler
    handleRenameSubmit,
    // Delete Folder Handler
    handleDeleteFolder,
    // Clipboard Handlers
    handleCopyProjects,
    handleCutProjects,
    handleCopyTestCases,
    handlePasteItems, // Renamed from handlePasteProject
    // Export Projects Handler
    handleExportProjects,
    // Delete Projects Handler
    handleDeleteProjects,
    // Import Project Handler
    handleImportProject,
    // Refresh Handler
    handleRefreshAll,
    // Edit Default CLID Handlers
    handleEditDefaultClid,
    handleEditClidSubmit,
    // Clipboard state derived for Menu (can be derived in Folders.tsx too)
    projectClipboardItems: items.filter((i) => i.kind === 'project'),
    testCaseClipboardItems: items.filter((i) => i.kind === 'testcase'),
    clipboardMode: mode,
    newFolderParentId, // Return the state for the modal
  };
} 
